import React, { useMemo, useState } from 'react';
import { Calendar, DollarSign, CheckCircle, XCircle, Plus, Eye, X, Pencil, Download } from 'lucide-react';
import { exportToCSV, exportToXLSX } from '../lib/exporters';
import DepositManager from './DepositManager';
import { useData } from '../providers/DataProvider';
import { computeOrderCashNeeded, computeOrderChargeBreakdown } from '../lib/orderCharges';

const PurchaseManagement = ({ transactions, suppliers, onUpdateTransactionDeposits, onMarkAsDelivered }) => {
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const data = (typeof useData === 'function') ? useData() : null;
  const branches = data?.branches || [];
  const branchBalances = Array.isArray(data?.branchBalances) ? data.branchBalances : [];
  const branchMovements = Array.isArray(data?.branchMovements) ? data.branchMovements : [];
  const currentUser = data?.currentUser || null;

  const getBranchKey = (item) => String(item?.branchId ?? item?.branch_id ?? '');
  const role = (currentUser?.role ?? '').toString().toLowerCase();
  const isAdmin = role === 'admin' || role.includes('admin');

  // Filtrar solo las transacciones de tipo 'order' (excluir canceladas)
  const orderTransactions = useMemo(() => {
    const txs = Array.isArray(transactions) ? transactions : [];
    return txs.filter((t) => {
      const type = (t.type || t.transaction_type || '').toString();
      if (type !== 'order') return false;
      const status = (t.status || '').toString();
      return status !== 'cancelled';
    });
  }, [transactions]);

  const getSupplierName = (id) => suppliers.find(s => s.id === id)?.name || 'N/A';

  // Calcular saldo disponible basado en las ganancias netas y efectivo en caja
  const { transactionProfits = [], branchProfits = [] } = data || {};

  // Branch cash helpers (mirror logic from Branches.js)
  const computeTxTotalsForBranch = (branchId) => {
    try {
      const txs = Array.isArray(transactions) ? transactions : [];
      const branchKey = String(branchId ?? '');
      if (!branchKey) return { entradas: 0, salidas: 0 };
      const filtered = txs.filter((t) => {
        const txBranchKey = getBranchKey(t);
        if (!txBranchKey || txBranchKey !== branchKey) return false;
        return String(t.status) !== 'cancelled';
      });
      // entradas: pedidos (suman a caja)
      const entradas = filtered.reduce((sum, t) => {
        const type = (t.type || t.transaction_type || '').toString();
        if (type !== 'order') return sum;
        const cashNeeded = computeOrderCashNeeded(t);
        const delta = Number.isFinite(cashNeeded) ? cashNeeded : Number(t.amount || 0) || 0;
        return sum + delta;
      }, 0);
      // salidas: ventas entregadas/completadas (restan de caja)
      const salidas = filtered.reduce((sum, t) => {
        const type = (t.type || t.transaction_type || '').toString();
        const status = (t.status || '').toString();
        const delivered = status === 'delivered' || status === 'completed' || status === 'paid';
        const saleOut = type === 'sale' && delivered ? Number(t.amount || 0) : 0;
        return sum + saleOut;
      }, 0);
      return { entradas, salidas };
    } catch {
      return { entradas: 0, salidas: 0 };
    }
  };

  const getBranchBalanceRow = (branchId) => {
    if (!branchId) return null;
    const row = branchBalances.find((r) => String(r.branch_id) === String(branchId));
    return row || null;
  };
  const computeMovementTotalsForBranch = (branchId) => {
    try {
      const ms = Array.isArray(branchMovements) ? branchMovements : [];
      const filtered = ms.filter((m) => String(m.branch_id) === String(branchId));
      const entradas = filtered.reduce((sum, m) => sum + (m.movement_type === 'entrada' ? Number(m.amount || 0) : 0), 0);
      const salidas = filtered.reduce((sum, m) => sum + (m.movement_type === 'salida' ? Number(m.amount || 0) : 0), 0);
      return { entradas, salidas };
    } catch {
      return { entradas: 0, salidas: 0 };
    }
  };

  const getBranchCash = (branch) => {
    if (!branch) return 0;
    const row = getBranchBalanceRow(branch?.id);
    if (row) return Number(row.cash_balance ?? 0) || 0;
    const { entradas, salidas } = computeTxTotalsForBranch(branch?.id);
    const mov = computeMovementTotalsForBranch(branch?.id);
    const baseline = Number((branch && (branch.initialCash != null ? branch.initialCash : branch.cashBalance)) || 0) || 0;
    return baseline + entradas - salidas + (mov.entradas - mov.salidas);
  };

  const selectedBranch = useMemo(() => {
    const list = Array.isArray(branches) ? branches : [];
    if (!list.length) return null;
    const userBranchKey = String(currentUser?.branchId ?? '');
    if (userBranchKey) {
      const matching = list.find((b) => getBranchKey(b) === userBranchKey);
      if (matching) return matching;
    }
    return list[0];
  }, [branches, currentUser]);
  const branchCash = selectedBranch ? getBranchCash(selectedBranch) : 0;
  // Global available balance = sum of all branch balances
  const globalAvailableBalance = useMemo(() => {
    if (Array.isArray(branchBalances) && branchBalances.length > 0) {
      return branchBalances.reduce((sum, r) => sum + (Number(r.cash_balance || 0) || 0), 0);
    }
    // Fallback: compute from local cash per branch
    const list = Array.isArray(branches) ? branches : [];
    return list.reduce((sum, b) => sum + getBranchCash(b), 0);
  }, [branchBalances, branches]);
  const visibleBranchKey = selectedBranch ? getBranchKey(selectedBranch) : '';
  
  // Obtener el saldo disponible de la sucursal actual
  const availableBalance = useMemo(() => {
    // Use global sum as requested
    return Number(globalAvailableBalance || 0);
  }, [globalAvailableBalance]);

  // User-editable suggested widgets as FLAT AMOUNTS (can exceed available cash)
  const [suggestedAmount, setSuggestedAmount] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const v = Number(window.localStorage.getItem('pm_suggested_amount'));
    return Number.isFinite(v) ? v : 0;
  });
  const [reserveAmount, setReserveAmount] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const v = Number(window.localStorage.getItem('pm_reserve_amount'));
    return Number.isFinite(v) ? v : 0;
  });
  const saveAmt = (key, val) => {
    try { if (typeof window !== 'undefined') window.localStorage.setItem(key, String(val)); } catch {}
  };

  const handleEditAmount = (storageKey, currentValue, label, setter) => {
    try {
      if (typeof window === 'undefined') return;
      const input = window.prompt(`Editar ${label}`, String(currentValue ?? 0));
      if (input == null) return;
      const parsed = Math.max(0, Number(input) || 0);
      setter(parsed);
      saveAmt(storageKey, parsed);
    } catch {}
  };

  const visibleOrders = useMemo(() => {
    if (!visibleBranchKey || isAdmin) return orderTransactions;
    return orderTransactions.filter((order) => getBranchKey(order) === visibleBranchKey);
  }, [orderTransactions, visibleBranchKey, isAdmin]);

  const handleViewDeposits = (purchase) => {
    setSelectedPurchase(purchase);
    setShowDepositModal(true);
  };

  const handleAddDeposit = (purchaseId, deposit) => {
    onUpdateTransactionDeposits(purchaseId, [...(selectedPurchase.deposits || []), deposit]);
    setSelectedPurchase(prev => ({ ...prev, deposits: [...(prev.deposits || []), deposit] }));
  };

  const handleDeleteDeposit = (purchaseId, depositId) => {
    const updatedDeposits = (selectedPurchase.deposits || []).filter(dep => dep.id !== depositId);
    onUpdateTransactionDeposits(purchaseId, updatedDeposits);
    setSelectedPurchase(prev => ({ ...prev, deposits: updatedDeposits }));
  };

  const buildOrderExportRows = (rows = visibleOrders) => {
    return rows.map((order) => {
      const totalDeposited = (order.deposits || []).reduce((sum, dep) => sum + Number(dep.amount || 0), 0);
      const breakdown = computeOrderChargeBreakdown(order);
      const { base, external, internal, totalCost, cashNeeded } = breakdown;
      return {
        id: order.id,
        fecha: order.date,
        proveedor: getSupplierName(order.supplierId),
        monto_base: base,
        costos_externos: external,
        comision_interna: internal,
        total_real: totalCost,
        impacto_caja: cashNeeded,
        depositado: totalDeposited,
        pendiente: Math.max(0, cashNeeded - totalDeposited),
        estado: order.status,
        origen_fondos: order.fundSource,
      };
    });
  };
  const exportOrdersCSV = () => {
    exportToCSV(`compras_${new Date().toISOString().slice(0,10)}`, buildOrderExportRows());
  };
  const exportOrdersXLSX = async () => {
    await exportToXLSX(`compras_${new Date().toISOString().slice(0,10)}`, [
      { name: 'Compras', data: buildOrderExportRows() },
    ]);
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Gestión de Compras</h1>
      
      {/* Panel de saldo Firsbel y saldo sucursal */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 mb-6 neon-border">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
          <DollarSign className="w-5 h-5 mr-2 text-green-600 dark:text-green-400" />
          Saldo Disponible para Compras
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/30 rounded-lg">
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">${availableBalance.toLocaleString('es-ES')}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Saldo Actual</p>
          </div>
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg relative">
            <button
              type="button"
              aria-label="Editar límite sugerido"
              onClick={() => handleEditAmount('pm_suggested_amount', suggestedAmount, 'Límite Sugerido ($)', setSuggestedAmount)}
              className="absolute top-2 right-2 p-2 rounded-lg text-blue-700 dark:text-blue-300 hover:bg-blue-100 hover:dark:bg-blue-800/50"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">${Number(suggestedAmount || 0).toLocaleString('es-ES')}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Límite Sugerido</p>
          </div>
          <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/30 rounded-lg relative">
            <button
              type="button"
              aria-label="Editar reserva recomendada"
              onClick={() => handleEditAmount('pm_reserve_amount', reserveAmount, 'Reserva Recomendada ($)', setReserveAmount)}
              className="absolute top-2 right-2 p-2 rounded-lg text-orange-700 dark:text-orange-300 hover:bg-orange-100 hover:dark:bg-orange-800/50"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">${Number(reserveAmount || 0).toLocaleString('es-ES')}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Reserva Recomendada</p>
          </div>
        </div>
        {selectedBranch && (
          <div className="mt-4 text-sm text-gray-700 dark:text-gray-200">
            Sucursal: <span className="font-medium">{selectedBranch.name}</span> • Saldo total: <span className="font-semibold">${availableBalance.toLocaleString('es-ES')}</span>
          </div>
        )}
        {availableBalance > 0 ? (
          <p className="mt-4 text-sm text-green-600 dark:text-green-400 font-medium flex items-center">
            <CheckCircle className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
            Fondos disponibles para realizar compras
          </p>
        ) : (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400 font-medium flex items-center">
            <XCircle className="w-4 h-4 mr-2 text-red-600 dark:text-red-400" />
            Sin fondos disponibles para compras
          </p>
        )}
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-xl  border border-gray-200 dark:border-gray-700 overflow-hidden neon-border">
        <div className="flex items-center justify-between px-6 pt-4 pb-2">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Órdenes</h2>
          <div className="flex items-center gap-2">
            <button onClick={exportOrdersCSV} className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700  hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"><Download className="h-4 w-4"/> CSV</button>
            <button onClick={exportOrdersXLSX} className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700  hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">Excel</button>
          </div>
        </div>
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Fecha</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Proveedor</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Monto</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Origen Fondos</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Depósitos</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Pendiente</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Estado</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {visibleOrders.map((order) => {
              const totalDeposited = (order.deposits || []).reduce((sum, dep) => sum + Number(dep.amount || 0), 0);
              const breakdown = computeOrderChargeBreakdown(order);
              const { base, external, internal, totalCost, cashNeeded } = breakdown;
              const pendingAmount = Math.max(0, cashNeeded - totalDeposited);
              return (
                <tr key={order.id} className="hover:bg-gray-50 hover:dark:bg-gray-700/60">
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{order.date}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{getSupplierName(order.supplierId)}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    <div className="font-medium">Total: ${cashNeeded.toLocaleString('es-ES')}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-300">Base: ${base.toLocaleString('es-ES')}</div>
                    {external > 0 && (
                      <div className="text-xs text-red-600 dark:text-red-300">Costos: +${external.toLocaleString('es-ES')}</div>
                    )}
                    {internal > 0 && (
                      <div className="text-xs text-green-600 dark:text-green-300">Descuento: -${internal.toLocaleString('es-ES')}</div>
                    )}
                    <div className="text-xs text-gray-500 dark:text-gray-300">Total real: ${totalCost.toLocaleString('es-ES')}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      order.fundSource === 'own' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                    }`}>
                      {order.fundSource === 'own' ? 'Fondos Propios' : 'Fondos Cliente'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">${totalDeposited.toLocaleString('es-ES')}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      pendingAmount > 0 ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    }`}>
                      ${pendingAmount.toLocaleString('es-ES')}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      order.status === 'pending' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    }`}>
                      {order.status === 'pending_deposit' ? 'Esperando Depósito' : 
                       order.status === 'pending_delivery' ? 'Por Entregar' :
                       order.status === 'delivered' ? 'Entregado' :
                       order.status === 'cancelled' ? 'Cancelado' : 'Pendiente'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button 
                      onClick={() => handleViewDeposits(order)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 hover:dark:text-blue-300 text-sm font-medium flex items-center mr-2"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Depósitos
                    </button>
                    {order.status === 'pending' && onMarkAsDelivered && (
                      <button 
                        onClick={() => onMarkAsDelivered(order.id)}
                        className="text-green-600 dark:text-green-400 hover:text-green-900 hover:dark:text-green-300 text-sm font-medium flex items-center"
                      >
                        <CheckCircle className="w-4 h-4 mr-1 text-green-600 dark:text-green-400" />
                        Entregado
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {visibleOrders.length === 0 && (
              <tr>
                <td colSpan="8" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  No hay compras registradas
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showDepositModal && selectedPurchase && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 w-full max-w-lg relative overflow-y-auto max-h-[90vh]">
            <button 
              onClick={() => setShowDepositModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:dark:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Gestión de Depósitos</h2>
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">Fecha: {selectedPurchase?.date || 'Sin fecha'}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Proveedor: {getSupplierName(selectedPurchase.supplierId)}</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Monto Total: ${selectedPurchase.amount.toLocaleString('es-ES')}</p>
            </div>
            
            <DepositManager 
              purchaseId={selectedPurchase.id}
              deposits={selectedPurchase.deposits || []}
              onAddDeposit={handleAddDeposit}
              onDeleteDeposit={handleDeleteDeposit}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseManagement;
